import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

/* ═══════════════════════════════════════════════════════════════
   Notifications SETRAG
   - Toasts stylés (succès, erreur, attention, info) empilés,
     fermeture manuelle, pause au survol, barre de progression.
   - Boîte de confirmation stylée qui remplace window.confirm() et
     renvoie une Promise<boolean>.
   - InlineAlert : message persistant dans un formulaire ou une page.
   Aucune dépendance externe, aucun console.*, aucun alert() natif.
═══════════════════════════════════════════════════════════════ */

const NotifyContext = createContext(null);

const TYPE_META = {
  success: { title: "Succès",      color: "#0f7a47", bg: "#ecf9f1", ring: "#a7e3c2" },
  error:   { title: "Erreur",      color: "#b42318", bg: "#fef1f0", ring: "#f6b7b1" },
  warning: { title: "Attention",   color: "#9a5b00", bg: "#fff8e6", ring: "#f3d58a" },
  info:    { title: "Information", color: "#1b4f8a", bg: "#eef5fd", ring: "#b5d0ef" },
};

const DEFAULT_DURATION = { success: 3800, info: 4200, warning: 5200, error: 6500 };

function Icon({ type, size = 18 }) {
  const p = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2.4, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true };
  if (type === "success") return <svg {...p}><path d="M20 6 9 17l-5-5" /></svg>;
  if (type === "error")   return <svg {...p}><path d="M18 6 6 18M6 6l12 12" /></svg>;
  if (type === "warning") return <svg {...p}><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>;
  if (type === "danger")  return <svg {...p}><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" /></svg>;
  return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>;
}

const css = `
.ntf-stack{position:fixed;top:16px;right:16px;z-index:10050;display:flex;flex-direction:column;gap:10px;width:min(380px,calc(100vw - 32px));pointer-events:none;}
.ntf-toast{pointer-events:auto;position:relative;overflow:hidden;display:flex;gap:12px;align-items:flex-start;
  background:#fff;border:1px solid var(--ntf-ring);border-left:4px solid var(--ntf-color);border-radius:14px;
  padding:13px 40px 15px 13px;box-shadow:0 14px 38px rgba(14,31,23,.16),0 2px 6px rgba(14,31,23,.06);
  font-family:'DM Sans','Segoe UI',system-ui,sans-serif;color:#0e1f17;animation:ntfIn .28s cubic-bezier(.2,.9,.3,1.2) both;}
.ntf-toast.leaving{animation:ntfOut .22s ease forwards;}
.ntf-ico{flex-shrink:0;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:var(--ntf-bg);color:var(--ntf-color);}
.ntf-body{min-width:0;flex:1;}
.ntf-title{font-weight:700;font-size:14px;line-height:1.3;color:var(--ntf-color);}
.ntf-msg{font-size:13.5px;line-height:1.45;color:#2b3a33;margin-top:2px;overflow-wrap:anywhere;}
.ntf-close{position:absolute;top:8px;right:8px;width:28px;height:28px;border:none;border-radius:8px;background:transparent;color:#7a8b82;cursor:pointer;display:flex;align-items:center;justify-content:center;}
.ntf-close:hover{background:#f1f1ec;color:#0e1f17;}
.ntf-bar{position:absolute;left:0;bottom:0;height:3px;background:var(--ntf-color);opacity:.35;transform-origin:left;animation:ntfBar linear forwards;}
.ntf-toast:hover .ntf-bar{animation-play-state:paused;}

.ntf-backdrop{position:fixed;inset:0;z-index:10040;background:rgba(10,24,16,.52);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;padding:20px;animation:ntfFade .18s ease both;}
.ntf-dialog{width:100%;max-width:440px;background:#fffefb;border-radius:20px;box-shadow:0 30px 80px rgba(0,0,0,.3);overflow:hidden;
  font-family:'DM Sans','Segoe UI',system-ui,sans-serif;color:#0e1f17;animation:ntfPop .24s cubic-bezier(.2,.9,.3,1.15) both;}
.ntf-dialog-head{display:flex;gap:14px;align-items:flex-start;padding:22px 22px 6px;}
.ntf-dialog-ico{flex-shrink:0;width:44px;height:44px;border-radius:14px;display:flex;align-items:center;justify-content:center;background:var(--ntf-bg);color:var(--ntf-color);}
.ntf-dialog-title{font-family:'Playfair Display',Georgia,serif;font-size:20px;font-weight:700;line-height:1.25;}
.ntf-dialog-msg{font-size:14px;line-height:1.55;color:#3d4c45;padding:6px 22px 4px 80px;}
.ntf-dialog-actions{display:flex;justify-content:flex-end;gap:10px;padding:18px 22px 22px;}
.ntf-btn{font-family:inherit;font-size:14px;font-weight:700;border-radius:11px;padding:11px 18px;cursor:pointer;border:1.5px solid transparent;transition:transform .12s,background .15s,box-shadow .15s;}
.ntf-btn:active{transform:translateY(1px);}
.ntf-btn-ghost{background:#fff;border-color:#e3dccb;color:#2b3a33;}
.ntf-btn-ghost:hover{background:#f5f0e5;}
.ntf-btn-primary{background:#1a3d2b;color:#fff;box-shadow:0 6px 16px rgba(26,61,43,.25);}
.ntf-btn-primary:hover{background:#0f2619;}
.ntf-btn-danger{background:#b42318;color:#fff;box-shadow:0 6px 16px rgba(180,35,24,.25);}
.ntf-btn-danger:hover{background:#912018;}
.ntf-btn:focus-visible,.ntf-close:focus-visible{outline:3px solid rgba(200,148,42,.6);outline-offset:2px;}

.ntf-inline{display:flex;gap:11px;align-items:flex-start;border-radius:12px;padding:12px 14px;margin-bottom:16px;
  background:var(--ntf-bg);border:1px solid var(--ntf-ring);color:#2b3a33;font-size:13.5px;line-height:1.5;}
.ntf-inline .ntf-ico{width:26px;height:26px;background:#fff;}
.ntf-inline strong.ntf-title{display:block;font-size:13.5px;}

@media (max-width:600px){
  .ntf-stack{top:auto;bottom:12px;right:12px;left:12px;width:auto;padding-bottom:env(safe-area-inset-bottom);}
  .ntf-backdrop{align-items:flex-end;padding:0;}
  .ntf-dialog{max-width:none;border-radius:22px 22px 0 0;padding-bottom:env(safe-area-inset-bottom);animation:ntfSheet .26s cubic-bezier(.2,.9,.3,1) both;}
  .ntf-dialog-msg{padding:4px 22px 4px;}
  .ntf-dialog-actions{flex-direction:column-reverse;}
  .ntf-btn{width:100%;padding:13px 18px;}
}
@keyframes ntfIn{from{opacity:0;transform:translateY(-10px) scale(.97);}to{opacity:1;transform:none;}}
@keyframes ntfOut{to{opacity:0;transform:translateX(24px);}}
@keyframes ntfBar{from{transform:scaleX(1);}to{transform:scaleX(0);}}
@keyframes ntfFade{from{opacity:0;}to{opacity:1;}}
@keyframes ntfPop{from{opacity:0;transform:translateY(10px) scale(.97);}to{opacity:1;transform:none;}}
@keyframes ntfSheet{from{transform:translateY(100%);}to{transform:none;}}
@media (prefers-reduced-motion:reduce){.ntf-toast,.ntf-dialog,.ntf-backdrop{animation:none;}.ntf-bar{display:none;}}
`;

let idSeq = 0;

function Toast({ t, onClose }) {
  const meta = TYPE_META[t.type] || TYPE_META.info;
  const timer = useRef(null);
  const remaining = useRef(t.duration);
  const startedAt = useRef(Date.now());

  const start = useCallback(() => {
    if (!t.duration) return;
    startedAt.current = Date.now();
    timer.current = setTimeout(() => onClose(t.id), remaining.current);
  }, [t.duration, t.id, onClose]);
  const pause = () => {
    if (!timer.current) return;
    clearTimeout(timer.current); timer.current = null;
    remaining.current -= Date.now() - startedAt.current;
  };

  useEffect(() => { start(); return () => clearTimeout(timer.current); }, [start]);

  return (
    <div className={`ntf-toast${t.leaving ? " leaving" : ""}`} role={t.type === "error" ? "alert" : "status"}
      style={{ "--ntf-color": meta.color, "--ntf-bg": meta.bg, "--ntf-ring": meta.ring }}
      onMouseEnter={pause} onMouseLeave={start}>
      <div className="ntf-ico"><Icon type={t.type} /></div>
      <div className="ntf-body">
        <div className="ntf-title">{t.title || meta.title}</div>
        {t.message && <div className="ntf-msg">{t.message}</div>}
      </div>
      <button className="ntf-close" aria-label="Fermer la notification" onClick={() => onClose(t.id)}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
      {t.duration > 0 && <div className="ntf-bar" style={{ animationDuration: `${t.duration}ms` }} />}
    </div>
  );
}

function ConfirmDialog({ dlg, onAnswer }) {
  const okRef = useRef(null);
  const tone = dlg.danger ? "danger" : (dlg.type || "info");
  const meta = dlg.danger ? TYPE_META.error : (TYPE_META[dlg.type] || { ...TYPE_META.info, color: "#1a3d2b", bg: "#e8f1ec" });

  useEffect(() => {
    okRef.current?.focus();
    const onKey = e => { if (e.key === "Escape") onAnswer(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onAnswer]);

  return (
    <div className="ntf-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) onAnswer(false); }}>
      <div className="ntf-dialog" role="alertdialog" aria-modal="true" aria-labelledby="ntf-dlg-title"
        style={{ "--ntf-color": meta.color, "--ntf-bg": meta.bg }}>
        <div className="ntf-dialog-head">
          <div className="ntf-dialog-ico"><Icon type={tone === "danger" ? "danger" : tone} size={22} /></div>
          <div className="ntf-dialog-title" id="ntf-dlg-title">{dlg.title}</div>
        </div>
        {dlg.message && <div className="ntf-dialog-msg">{dlg.message}</div>}
        <div className="ntf-dialog-actions">
          <button className="ntf-btn ntf-btn-ghost" onClick={() => onAnswer(false)}>{dlg.cancelLabel || "Annuler"}</button>
          <button ref={okRef} className={`ntf-btn ${dlg.danger ? "ntf-btn-danger" : "ntf-btn-primary"}`} onClick={() => onAnswer(true)}>
            {dlg.confirmLabel || "Confirmer"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function NotifyProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [dialog, setDialog] = useState(null);
  const resolver = useRef(null);

  const close = useCallback(id => {
    setToasts(list => list.map(t => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => setToasts(list => list.filter(t => t.id !== id)), 220);
  }, []);

  const push = useCallback((type, message, opts = {}) => {
    const text = typeof message === "string" ? message.trim() : message;
    const id = ++idSeq;
    setToasts(list => {
      // Évite d'empiler deux fois exactement le même message.
      const rest = list.filter(t => !(t.type === type && t.message === text && !t.leaving));
      return [...rest.slice(-3), { id, type, message: text, title: opts.title, duration: opts.duration ?? DEFAULT_DURATION[type] }];
    });
    return id;
  }, []);

  const confirm = useCallback(options => new Promise(resolve => {
    if (resolver.current) resolver.current(false);
    resolver.current = resolve;
    setDialog(typeof options === "string" ? { title: options } : options);
  }), []);

  const answer = useCallback(value => {
    resolver.current?.(value);
    resolver.current = null;
    setDialog(null);
  }, []);

  const api = useMemo(() => ({
    success: (m, o) => push("success", m, o),
    error:   (m, o) => push("error", m, o),
    warning: (m, o) => push("warning", m, o),
    info:    (m, o) => push("info", m, o),
    show:    (type, m, o) => push(TYPE_META[type] ? type : "info", m, o),
    dismiss: close,
    confirm,
  }), [push, close, confirm]);

  return (
    <NotifyContext.Provider value={api}>
      <style>{css}</style>
      {children}
      <div className="ntf-stack" aria-live="polite">
        {toasts.map(t => <Toast key={t.id} t={t} onClose={close} />)}
      </div>
      {dialog && <ConfirmDialog dlg={dialog} onAnswer={answer} />}
    </NotifyContext.Provider>
  );
}

const fallback = { success() {}, error() {}, warning() {}, info() {}, show() {}, dismiss() {}, confirm: () => Promise.resolve(false) };

export function useNotify() {
  return useContext(NotifyContext) || fallback;
}

/** Compatibilité avec l'ancienne signature showToast(message, type). */
export function useShowToast() {
  const notify = useNotify();
  return useCallback((msg, type = "error") => notify.show(type, msg), [notify]);
}

/** Message d'alerte persistant (formulaires, états de page). */
export function InlineAlert({ type = "error", title, children, style }) {
  const meta = TYPE_META[type] || TYPE_META.info;
  return (
    <div className="ntf-inline" role={type === "error" ? "alert" : "status"}
      style={{ "--ntf-color": meta.color, "--ntf-bg": meta.bg, "--ntf-ring": meta.ring, ...style }}>
      <div className="ntf-ico" style={{ color: meta.color }}><Icon type={type} size={15} /></div>
      <div>
        {title && <strong className="ntf-title" style={{ color: meta.color }}>{title}</strong>}
        <div>{children}</div>
      </div>
    </div>
  );
}

/** Extrait un message lisible d'une erreur axios / JS. */
export function errorMessage(e, fallbackMsg = "Une erreur est survenue. Réessayez.") {
  const d = e?.response?.data;
  if (typeof d === "string" && d.length < 200) return d;
  return d?.error || d?.message || d?.detail || (e?.message && !/^Request failed/.test(e.message) ? e.message : null) || fallbackMsg;
}
